import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { onBillingUsageChanged, onPlanChanged } from '@/lib/billingEvents';
import type { BillingStatus } from '@/types';

/**
 * Polls GET /companies/:id/billing/status every 60s.
 * Returns null until a company is selected.
 *
 * Sprint 2 (22/05/26 ~18:00) — Fix G: also subscribes to `plan-changed`
 * events so the cache invalidates immediately after an upgrade / cancel
 * / portal return without waiting up to 60s for the next poll. Previously
 * only `usage-changed` was wired (Alessandro's 5482dd6) which covers
 * AI consumption but NOT plan transitions.
 */
export function useBillingStatus() {
  const { selectedCompany, isAuthenticated } = useAuth();
  const companyId = selectedCompany?.id;

  const query = useQuery<BillingStatus | null>({
    queryKey: ['billing', 'status', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      return api.billing.getStatus(companyId);
    },
    enabled: !!companyId && isAuthenticated,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  useEffect(() => {
    const offUsage = onBillingUsageChanged(() => {
      void query.refetch();
    });
    const offPlan = onPlanChanged(() => {
      void query.refetch();
    });
    return () => {
      offUsage();
      offPlan();
    };
  }, [query.refetch]);

  return query;
}